import { useRef } from 'react';
import { FORM_ITEM_CATEGORY_OPTIONS } from '../../utils/constants';

type FormCategoryOption = (typeof FORM_ITEM_CATEGORY_OPTIONS)[number];

export type FormCategoryProps = {
    value?: string | null;
    onChange: (value: string) => void;
    options?: readonly FormCategoryOption[];
};

export const FormCategory = ({ value, onChange, options = FORM_ITEM_CATEGORY_OPTIONS }: FormCategoryProps) => {
    const optionRefs = useRef<Array<HTMLButtonElement | null>>([]);
    const selectedIndex = Math.max(
        0,
        options.findIndex((cat) => cat.value === value),
    );

    const focusOption = (index: number) => {
        const next = options[index];
        if (!next) return;
        onChange(next.value);
        requestAnimationFrame(() => optionRefs.current[index]?.focus());
    };

    const onOptionKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
        const last = options.length - 1;
        switch (event.key) {
            case 'ArrowRight':
            case 'ArrowDown':
                event.preventDefault();
                focusOption(index === last ? 0 : index + 1);
                break;
            case 'ArrowLeft':
            case 'ArrowUp':
                event.preventDefault();
                focusOption(index === 0 ? last : index - 1);
                break;
            case 'Home':
                event.preventDefault();
                focusOption(0);
                break;
            case 'End':
                event.preventDefault();
                focusOption(last);
                break;
            default:
                break;
        }
    };

    return (
        <div className="item-details-section category-detail-section">
            <h4 className="item-section-label">Category</h4>
            <div className="category-cards" role="radiogroup" aria-label="Item category">
                {options.map((cat, index) => {
                    const selected = value === cat.value;
                    return (
                        <button
                            key={cat.value}
                            ref={(node) => {
                                optionRefs.current[index] = node;
                            }}
                            type="button"
                            role="radio"
                            aria-checked={selected}
                            tabIndex={index === selectedIndex ? 0 : -1}
                            className={[
                                'category-card',
                                `category-card-${cat.statusTone}`,
                                selected ? 'active' : '',
                            ]
                                .filter(Boolean)
                                .join(' ')}
                            onClick={() => onChange(cat.value)}
                            onKeyDown={(event) => onOptionKeyDown(event, index)}
                        >
                            <span className="category-card-title">{cat.label}</span>
                            <span className="category-card-description">{cat.description}</span>
                        </button>
                    );
                })}
            </div>
        </div>
    );
};
